import { cn } from '@/lib/cn';
import type { PresenceStatus } from '@/types/social';

interface PresenceBadgeProps {
  status: PresenceStatus;
  /** When true, render a tiny pill with the label next to the dot. */
  label?: boolean;
  className?: string;
}

const STATUS_LABELS: Record<PresenceStatus, string> = {
  online: 'Online',
  idle: 'Idle',
  watching: 'Watching',
  in_tournament: 'In tournament',
  offline: 'Offline',
};

/**
 * Dot indicator for a user's current QSocials presence. Colours intentionally
 * mirror the QLASH dark palette + brand so the badge reads against
 * `bg-bg-secondary` / `bg-bg-tertiary` surfaces.
 *
 *   online        → brand (cyan)
 *   watching      → blue
 *   in_tournament → violet
 *   idle          → amber
 *   offline       → muted neutral
 */
const DOT_COLOR: Record<PresenceStatus, string> = {
  online: 'bg-brand shadow-[0_0_6px_rgba(0,231,223,0.65)]',
  watching: 'bg-sky-400 shadow-[0_0_6px_rgba(56,189,248,0.6)]',
  in_tournament: 'bg-violet-400 shadow-[0_0_6px_rgba(167,139,250,0.6)]',
  idle: 'bg-amber-400',
  offline: 'bg-fg-muted/40',
};

export function PresenceBadge({ status, label, className }: PresenceBadgeProps) {
  if (!label) {
    return (
      <span
        aria-label={STATUS_LABELS[status]}
        className={cn(
          'inline-block h-2.5 w-2.5 rounded-full',
          DOT_COLOR[status],
          className,
        )}
      />
    );
  }
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full bg-bg-tertiary px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-fg-muted',
        className,
      )}
    >
      <span className={cn('h-2 w-2 rounded-full', DOT_COLOR[status])} />
      {STATUS_LABELS[status]}
    </span>
  );
}
